import React from "react";

import { Column } from "Components/AppGrid/shipeezi-new-grid";
import { DefaultTooltip } from "Components/Generic/Tooltip";
import { Customer } from "Models/customer";
import { Job, JobLocation } from "Models/job";
import { Location } from "Models/location";
import { TFunction } from "react-i18next";
import { calculateTotalVolume } from "Util/calculateTotalVolume";
import { handleGetConvertValue, Metric } from "Util/converts";
import { ItemJobSubStatus } from "Util/enums";
import { formatJobLocation } from "Util/formatItemLocation";

import BadgeSubStatus from "../../../../../../../../components/Generic/BadgeSubStatus";
import BadgeType from "../../../../../../../../components/Generic/BadgeType";
import { ContainerLocation } from "./styles";

interface GetSubcontractorTableColumns {
  translate: TFunction<"translation", undefined>;
  disableCustomerSorting: boolean;
  setSorting: any;
  disableOriginSorting: boolean;
  setDisableOriginSorting: any;
  setDisableCustomerSorting: any;
  setDisableDestinationSorting: any;
  disableDestinationSorting: any;
  measurement: any;
}

export const getOrdersTableColumns = (props: GetSubcontractorTableColumns) => {
  const {
    translate,
    disableCustomerSorting,
    disableOriginSorting,
    setSorting,
    setDisableOriginSorting,
    setDisableCustomerSorting,
    setDisableDestinationSorting,
    disableDestinationSorting,
    measurement,
  } = props;

  const renderTextLocation = (locations?: Location[]) => {
    if (locations && locations?.length > 0)
      return locations[0]?.longAddress?.toString()?.trim() || "";

    return "";
  };

  const showBadgeType = (value?: string) => {
    return <BadgeType value={value} />;
  };

  const showBadgeSubStatus = (value?: string) => {
    return <BadgeSubStatus value={value} />;
  };

  const renderOrigin = (locations?: Location[]) => {
    return (
      <ContainerLocation>
        <div className="flex-location">
          <div className="div-location-ellipsis">
            <span>{renderTextLocation(locations)}</span>
          </div>
        </div>
      </ContainerLocation>
    );
  };

  const renderDestination = (locations?: Location[]) => {
    return (
      <ContainerLocation>
        <div className="flex-location">
          <div className="div-location-ellipsis">
            <span>{renderTextLocation(locations)}</span>
          </div>
        </div>
      </ContainerLocation>
    );
  };

  const showLocations = (jobLocations: JobLocation) => {
    if (jobLocations) {
      const uniques = formatJobLocation(jobLocations);

      const renderLocations = (location: string) => <div key={location}>{location}</div>;

      const tooltipSpread = uniques.map((location) => {
        return renderLocations(location);
      });

      return uniques && uniques?.length ? (
        <DefaultTooltip title={tooltipSpread} placement="top-start">
          <div>
            {uniques.map((location) => {
              return location !== "" ? renderLocations(location) : <></>;
            })}
          </div>
        </DefaultTooltip>
      ) : (
        <div>-</div>
      );
    }

    return "";
  };

  const columns: Column[] = [
    {
      name: "type",
      title: translate("type"),
      formatComponent: showBadgeType,
      hideSearch: true,
    },
    {
      name: "mode",
      title: translate("mode"),
      hideSearch: true,
      formatComponent: (value: string) =>
        value ? translate(value.toLowerCase()) : "",
    },
    { name: "reference", title: translate("reference") },
    {
      name: "customer",
      title: translate("customer"),
      hideSort: disableCustomerSorting,
      formatText: (customer: Customer) => (customer?.fullName ? customer.fullName : ""),
      associationColumnSettings: {
        whenSearching: {
          clearSorting: setSorting,
          disableSorting: setDisableCustomerSorting,
        },
        columnToSorting: "full_name",
      },
    },
    {
      name: "volume",
      title: translate("volume"),
      hideSort: true,
      hideSearch: true,
      formatComponent: (_, job: Job) => {
        if (job?.customVolumeActual === null || job?.customVolumeActual === undefined)
          return calculateTotalVolume(
            ItemJobSubStatus.ACTUAL,
            measurement,
            job?.itemsJob,
            job?.materials
          );

        return `${handleGetConvertValue(
          job?.customVolumeActual || 0,
          measurement?.code,
          measurement?.decimalPlaces
        )} ${measurement?.code || Metric["m³"]}`.trim();
      },
    },
    {
      name: "origins",
      title: translate("origin"),
      hideSort: disableOriginSorting,
      formatComponent: renderOrigin,
      associationColumnSettings: {
        whenSearching: {
          clearSorting: setSorting,
          disableSorting: setDisableOriginSorting,
        },
        columnToSorting: "long_address",
      },
    },
    {
      name: "destinations",
      title: translate("destination"),
      hideSort: disableDestinationSorting,
      formatComponent: renderDestination,
      associationColumnSettings: {
        whenSearching: {
          clearSorting: setSorting,
          disableSorting: setDisableDestinationSorting,
        },
        columnToSorting: "long_address",
      },
    },
    {
      name: "jobLocations",
      title: translate("location"),
      formatComponent: showLocations,
      hideSort: true,
      hideSearch: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: showBadgeSubStatus,
      hideSearch: true,
    },
  ];

  return columns;
};
